<script lang="ts">
  import { onMount } from "svelte";
  import type { TrainingState } from "../../../imports/builders";

  import TabulatorBare from "../../UC-tabulator-bare.svelte";
  import TrainingLegend from "./UC-TrainingLegend.svelte";
  import TrainingSummary from "./UC-training-summary.svelte";

  import {
    dagTrainingStatusIconFormatter,
    dagTrainingStatusIconDownloadAccessor,
    dagEmailLinkFormatter,
  } from "../../../shared/utils";

  import { getTrainingTallies } from "./fn";

  interface Props {
    trainingDashboardState: TrainingState;
    dataUrl: string;
    moreDataUrl: string;
  }

  let { trainingDashboardState, dataUrl, moreDataUrl }: Props = $props();

  let tableData = $state([]);

  // const tableTallies = $state({
  //   total: 0,
  //   complete: 0,
  //   notComplete: 0,
  //   exempt: 0,
  // })

  // export let moreDataUrl = undefined

  const maxPageFetches = 50;

  let fieldToFilter = $state("TrainingStatusIconId");

  // We'll bind this to the instance of TabulatorBare so we can call its exported download function from exportData()
  let tabulatorComponentRef;

  const tableColumns = [
    { title: "<span class='title'>M#</span>", field: "UCID", minWidth: 120 },
    { title: "<span class='title'>Last Name</span>", field: "LastName", minWidth: 140 },
    { title: "<span class='title'>First Name</span>", field: "FirstName", minWidth: 140 },
    { title: "<span class='title'>Org. Unit</span>", field: "OrgUnit", visible: false, download: true },
    {
      title: "<span class='title'>Position Title</span>",
      field: "PositionTitle",
      visible: false,
      download: true,
    },
    {
      title: "<span class='title'>Status</span>",
      field: "TrainingStatusIconId",
      formatter: dagTrainingStatusIconFormatter,
      accessorDownload: dagTrainingStatusIconDownloadAccessor,
      minWidth: 120,
    },
    {
      title: "<span class='title'>Email</span>",
      field: "Email",
      minWidth: 140,
      formatter: dagEmailLinkFormatter,
    },
  ];

  let parentFilterArray: string[] = $state([]);

  let tabulatorSelectedSummaryFilter: string = $state("totalEnrolled");
  const tabulatorSelectedSummaryFilterIntegers = {
    totalEnrolled: null,
    numberNotComplete: 1,
    numberComplete: 2,
    numberExceeded: 3,
    numberExempt: 4,
  };

  let tableFilterValue = $state("");
  let tableFilterFieldChoice = $state("LastName");

  // This $effect (formerly Svelte4 reactive statement) is meant to handle all filtering on this table.
  $effect(() => {
    // For any filter input change, let's gather the data, compose our array of filters, then set them all at once.
    let filtersToSet = [];

    // our table summary clickable filter
    if (
      tabulatorSelectedSummaryFilterIntegers[tabulatorSelectedSummaryFilter] &&
      tabulatorSelectedSummaryFilterIntegers[tabulatorSelectedSummaryFilter] !==
        2
    ) {
      filtersToSet.push({
        field: fieldToFilter,
        type: "=",
        value:
          tabulatorSelectedSummaryFilterIntegers[
            tabulatorSelectedSummaryFilter
          ],
      });
      // if it's complete (2), we need to also include the folks who went above and beyond (3)
      // we do this in Tabulator by adding an array of filters to OR between.
      // http://tabulator.info/docs/5.3/filter#func-complex
    } else if (
      tabulatorSelectedSummaryFilterIntegers[tabulatorSelectedSummaryFilter] ===
      2
    ) {
      filtersToSet.push([
        {
          field: fieldToFilter,
          type: "=",
          value: 2,
        },
        {
          field: fieldToFilter,
          type: "=",
          value: 3,
        },
      ]);
    }

    // our table header/arbitrary string filter
    if (tableFilterValue.length) {
      filtersToSet.push({
        field: tableFilterFieldChoice,
        type: "like",
        value: tableFilterValue,
      });
    }

    if (filtersToSet.length) {
      parentFilterArray = filtersToSet;
    } else {
      parentFilterArray = [];
    }
  }); //$effect

  let selectEle;
  let selectedText: string = $derived(
    selectEle?.options[selectEle.selectedIndex].text ?? ""
  );

  const exportData = () => {
    if (tabulatorComponentRef) {
      tabulatorComponentRef.triggerDownload("training-data-export");
    }
  };

  const fetchOnePage = async (url) => {
    const res = await fetch(url);
    return await res.json();
  };

  const fetchRemainingData = async (url) => {
    const pageRegex = /\?page=\d+/g;

    let sentinel = false;
    let count = 1; // first page has already been grabbed — may as well count it

    while (!sentinel) {
      count++;

      // grab a page
      const data = await fetchOnePage(url);
      tableData = [...tableData, ...data.data];

      // process data and update UI
      // const tallies = await getTrainingTallies(data)
      // console.log('tallies', tallies)

      // tableTallies.total += tallies.total
      // tableTallies.complete += tallies.complete
      // tableTallies.notComplete += tallies.notComplete
      // tableTallies.exempt += tallies.exempt

      if (tabulatorComponentRef) {
        tabulatorComponentRef.addData(data.data);
      }

      // if it's the last page, the job is done. Otherwise, we'll keep looping.
      if (data.isLastPage === "true") {
        sentinel = true;
      } else if (data.hasOwnProperty("isLastPage")) {
        // data seems correct. Determine next page URL, and
        const pageParam = url.match(pageRegex)[0];
        const pageNum = Number(pageParam.charAt(pageParam.length - 1));
        url = url.replace(pageRegex, `?page=${pageNum + 1}`);
      } else if (!data.hasOwnProperty("isLastPage") || count > maxPageFetches) {
        // if we get to so many pages, something might be broken
        sentinel = true;
        console.error(
          `Fetching has ended because we've exceeded the maximum allowed (${maxPageFetches}) fetches, or aren't receiving an 'isLastPage' value.`
        );
      } else {
        sentinel = true;
        console.error(`Fetching has ended for unforseen reasons.`);
      }

      // It's a 'while' loop, and this is the end of its instructions. If we make it here with a falsey
      // sentinel, we go back to the top, but with 'url' now containing the next page to grab.
    }
  };

  let tableTallies = $derived(getTrainingTallies(tableData));

  $inspect("data and tallies", {
    tableData,
    tableTallies,
  });

  onMount(async () => {
    const dataReq = await fetch(dataUrl);
    const data = await dataReq.json();

    tableData = data.data;
    tabulatorComponentRef.addData(data.data);

    if (data.isLastPage === "false") {
      fetchRemainingData(moreDataUrl);
    }
  });
</script>

<TrainingSummary
  bind:selectedFilter={tabulatorSelectedSummaryFilter}
  totalEnrolled={tableTallies.total}
  numberComplete={tableTallies.complete}
  numberNotComplete={tableTallies.notComplete}
  numberExempt={tableTallies.exempt}
/>
<div class="uc-tabulator training-table" aria-live="polite">
  <header>
    <div class="table-title">
      <h3>Training Data</h3>
    </div>

    <div class="table-filter">
      <select
        name="field-choice"
        aria-label="Select category to filter by"
        id="field-select"
        bind:value={tableFilterFieldChoice}
        bind:this={selectEle}
      >
        <option value="FirstName">First Name</option>
        <option value="LastName">Last Name</option>
        <option value="UCID">M#</option>
      </select>
      <input
        id="name"
        aria-label="Filter by {selectedText}"
        type="text"
        placeholder="Filter by {selectedText}  "
        bind:value={tableFilterValue}
      />
    </div>
  </header>
  <TabulatorBare
    bind:this={tabulatorComponentRef}
    data={tableData}
    columns={tableColumns}
    filterArray={parentFilterArray}
  />
  <TrainingLegend />
</div>
<div class="export-wrap">
  <button onclick={exportData}> Download CSV </button>
</div>
<span class="hidden tabulator-row tabulator-cell sr-only"></span>

<style>
  h3 {
    color: var(--uc-red);
    font-size: 3rem;
    font-weight: 600;
    padding: 2rem 0;
  }
  select {
    padding: 0.5rem 1.5rem;
    margin: 0 1.5rem 1.5rem;
    border: solid 1px #ccc;
    border-radius: 5px;
  }

  @media (max-width: 768px) {
    select {
      margin: 1.5rem 1.5rem 1.5rem 0;
    }
  }

  @media (max-width: 680px) {
    select {
      margin: 1.5rem 0;
    }
  }

  input[type="text"] {
    font-size: 2rem;
    position: relative;
    z-index: 1;
    border-radius: 5px;
    padding: 0.5rem 1.5rem;
    margin: 0 1.5rem 1.5rem;
    border: solid 1px #ccc;
    height: 4.6rem;
    flex-grow: 1;
  }

  @media (max-width: 768px) {
    input[type="text"] {
      margin: 1.5rem;
    }
  }
  @media (max-width: 529px) {
    input[type="text"] {
      margin: 0 0 1.5rem;
    }
  }

  .uc-tabulator {
    border-radius: 1rem;
    background-color: #fff;
    filter: drop-shadow(0.2em 0.2em 0.2em rgba(0, 0, 0, 0.15));
    margin-bottom: 3rem;
  }

  .sr-only {
    display: none;
  }

  header {
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;
  }

  @media (max-width: 768px) {
    header {
      flex-direction: column;
      align-items: center;
    }
  }

  .table-filter {
    display: flex;
    justify-content: space-evenly;
    flex-wrap: wrap;
    padding: 2rem 0rem 0rem;
  }

  @media (max-width: 768px) {
    .table-filter {
      display: flex;
      justify-content: left;
      flex-wrap: wrap;
    }
  }

  @media (max-width: 600px) {
    .table-filter {
      margin: 1rem;
      flex-direction: column;
    }
  }

  .table-title {
    color: var(--uc-red);
    padding: 0 2rem;
  }

  .export-wrap {
    text-align: right;
  }

  .export-wrap button {
    background-color: var(--uc-red);
    border: 1px solid #fff;
    color: #fff;
    border-radius: 0.25em;
  }
</style>
